// Global Instructions Rule Applied!
//
// A displayed figure that admits when it is not the whole number.
//
// cfg.number_formats.max_decimals is a DISPLAY setting and is allowed to be
// coarser than the value behind it — a valuation shown without decimals, an
// FX-converted amount carried at full precision and shown to the cent. That is
// deliberate, but it leaves the reader unable to tell an exact figure from an
// abbreviated one.
//
// So: render the policy's figure, and ONLY when it is not the whole story give
// it a dotted underline with the full value in a Tooltip — the same affordance
// the follow-up ledger uses for reworded text (FRONTEND_DESIGN_PATTERNS.md).
// An exact figure carries no mark at all, which is what makes the mark mean
// something; a column of clean amounts stays completely quiet.
//
// Scroll was considered and rejected for figures: it breaks right-alignment,
// which is what makes a column of numbers scannable.
//
// useNumberDisplay stays for the string cases — Excel exports, template
// literals, anywhere a ReactNode will not do.
//
// "Full" means everything the CLIENT holds. A figure arrives as a JSON number,
// i.e. a double, so a Postgres numeric carrying more than ~17 significant
// digits has already been rounded at the API boundary and this tooltip cannot
// recover it. That limit is upstream of the display; nothing here can widen
// it, and no money figure comes close.

import React from 'react'
import { Tooltip } from 'antd'
import {
  formatFull,
  formatWithPolicy,
  isAbbreviated,
  useNumberPolicy
} from '../utils/inputNumberFormat.ts'

export interface NumberTextProps {
  value: number | null | undefined
  /** cfg.number_formats key — 'currency' (default), 'unit_price', 'unit_count', 'fx_rate' */
  formatKey?: string
  /** rendered when the value is null/undefined */
  blank?: React.ReactNode
  className?: string
  /** wraps the figure, e.g. antd <Text strong> on a totals row */
  wrap?: (formatted: React.ReactNode) => React.ReactNode
}

export default function NumberText({
  value,
  formatKey = 'currency',
  blank = '—',
  className,
  wrap
}: NumberTextProps) {
  const policy = useNumberPolicy(formatKey)

  if (value == null) return <>{blank}</>

  const shown = formatWithPolicy(value, policy)
  const body = wrap ? wrap(shown) : shown

  if (!isAbbreviated(value, policy)) {
    return className ? <span className={className}>{body}</span> : <>{body}</>
  }

  return (
    <Tooltip title={formatFull(value, policy)}>
      <span
        className={className}
        // dotted underline = "there is more here", per the house convention
        style={{ textDecoration: 'underline dotted', textUnderlineOffset: '2px', cursor: 'help' }}
      >
        {body}
      </span>
    </Tooltip>
  )
}
